import { useCallback, useEffect, useRef, useState } from 'react'
import {
  enqueueSpeak,
  getPlayingKey,
  speakText,
  stopSpeaking,
  subscribePlayingKey,
  transcribeBlob,
  unlockAudioPlayback,
} from '../lib/voice'

const MUTE_KEY = 'rheya.voice.muted'
/** Auto-stop after this much silence once speech was heard (ms). */
const SILENCE_MS = 2800
const MIN_RECORD_MS = 900
const LEVEL_BARS = 24

export type SpeakAssistantOptions = {
  holdUntilPlay?: boolean
}

/**
 * Web Rheya captioned voice: queue TTS, hold bubbles until play, mute,
 * and mic → STT via MediaRecorder.
 */
export function useRheyaVoice(options?: { autoSpeak?: boolean }) {
  const autoSpeak = options?.autoSpeak !== false
  const [muted, setMutedState] = useState(() => {
    try {
      return localStorage.getItem(MUTE_KEY) === '1'
    } catch {
      return false
    }
  })
  const [speaking, setSpeaking] = useState(false)
  const [playingKey, setPlayingKey] = useState<string | null>(getPlayingKey())
  const [voiceError, setVoiceError] = useState<string | null>(null)
  const [heldKeys, setHeldKeys] = useState<Set<string>>(() => new Set())
  const [recording, setRecording] = useState(false)
  const [voiceBusy, setVoiceBusy] = useState(false)
  const [levels, setLevels] = useState<number[]>(() => Array(LEVEL_BARS).fill(0.08))

  const mediaRecorder = useRef<MediaRecorder | null>(null)
  const mediaStream = useRef<MediaStream | null>(null)
  const chunks = useRef<BlobPart[]>([])
  const analyser = useRef<AnalyserNode | null>(null)
  const audioCtx = useRef<AudioContext | null>(null)
  const levelTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAt = useRef(0)
  const heardSpeech = useRef(false)
  const lastSpeechAt = useRef<number | null>(null)
  const autoSendLock = useRef(false)
  const onAutoSendRef = useRef<((text: string) => void) | null>(null)

  useEffect(() => {
    return subscribePlayingKey((key) => {
      setPlayingKey(key)
      setSpeaking(key != null)
    })
  }, [])

  const releaseHeld = useCallback((key: string) => {
    setHeldKeys((prev) => {
      if (!prev.has(key)) return prev
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }, [])

  const releaseAllHeld = useCallback(() => {
    setHeldKeys((prev) => (prev.size === 0 ? prev : new Set()))
  }, [])

  const clearMeter = useCallback(() => {
    if (levelTimer.current) {
      clearInterval(levelTimer.current)
      levelTimer.current = null
    }
    setLevels(Array(LEVEL_BARS).fill(0.08))
  }, [])

  const stopMediaTracks = useCallback(() => {
    mediaStream.current?.getTracks().forEach((t) => t.stop())
    mediaStream.current = null
    try {
      void audioCtx.current?.close()
    } catch {
      // ignore
    }
    audioCtx.current = null
    analyser.current = null
  }, [])

  const setMuted = useCallback(
    (next: boolean) => {
      setMutedState(next)
      try {
        localStorage.setItem(MUTE_KEY, next ? '1' : '0')
      } catch {
        // ignore
      }
      if (next) {
        void stopSpeaking().then(() => {
          setSpeaking(false)
          releaseAllHeld()
        })
      } else {
        // Unmute click: sync unlock (must not be after await).
        unlockAudioPlayback()
      }
    },
    [releaseAllHeld],
  )

  const toggleMute = useCallback(() => {
    setMuted(!muted)
  }, [muted, setMuted])

  const speakAssistant = useCallback(
    (text: string, key?: string, opts?: SpeakAssistantOptions) => {
      const body = text.trim()
      if (!body || !autoSpeak || muted) return
      setVoiceError(null)
      const hold = opts?.holdUntilPlay !== false && !!key
      if (hold && key) {
        setHeldKeys((prev) => {
          if (prev.has(key)) return prev
          const next = new Set(prev)
          next.add(key)
          return next
        })
      }
      enqueueSpeak(body, key, {
        onReveal: key ? () => releaseHeld(key) : undefined,
        onError: (message) => setVoiceError(message),
      })
    },
    [autoSpeak, muted, releaseHeld],
  )

  /**
   * Must stay non-async: Chrome drops the user-gesture token if the click
   * handler is `async`, which blocks later play() after the TTS fetch.
   * @see https://developer.chrome.com/blog/play-request-was-interrupted
   */
  const togglePlayMessage = useCallback((text: string, key: string) => {
    const body = text.trim()
    if (!body) return
    setVoiceError(null)
    // Synchronous gesture work first.
    unlockAudioPlayback()
    void speakText(body, key).catch((err) => {
      setVoiceError(err instanceof Error ? err.message : 'Could not play voice')
      setSpeaking(false)
    })
  }, [])

  const stop = useCallback(async () => {
    await stopSpeaking()
    setSpeaking(false)
    releaseAllHeld()
  }, [releaseAllHeld])

  const finalizeRecording = useCallback(
    async (mode: 'send' | 'cancel'): Promise<string | null> => {
      if (mode === 'send') autoSendLock.current = true
      clearMeter()

      const recorder = mediaRecorder.current
      if (!recorder || recorder.state === 'inactive') {
        setRecording(false)
        stopMediaTracks()
        autoSendLock.current = false
        return null
      }

      const blob = await new Promise<Blob | null>((resolve) => {
        recorder.onstop = () => {
          const parts = chunks.current
          chunks.current = []
          if (parts.length === 0) {
            resolve(null)
            return
          }
          resolve(new Blob(parts, { type: recorder.mimeType || 'audio/webm' }))
        }
        try {
          recorder.stop()
        } catch {
          resolve(null)
        }
      })

      mediaRecorder.current = null
      stopMediaTracks()
      setRecording(false)

      if (mode === 'cancel' || !blob) {
        autoSendLock.current = false
        return null
      }

      setVoiceBusy(true)
      setVoiceError(null)
      try {
        const text = await transcribeBlob(blob, 'recording.webm')
        return text || null
      } catch (err) {
        setVoiceError(err instanceof Error ? err.message : 'Could not transcribe')
        return null
      } finally {
        setVoiceBusy(false)
        autoSendLock.current = false
      }
    },
    [clearMeter, stopMediaTracks],
  )

  const startRecording = useCallback(async () => {
    setVoiceError(null)
    autoSendLock.current = false
    try {
      await unlockAudioPlayback()
      await stopSpeaking()
      setSpeaking(false)

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStream.current = stream

      const ctx = new AudioContext()
      audioCtx.current = ctx
      const source = ctx.createMediaStreamSource(stream)
      const anal = ctx.createAnalyser()
      anal.fftSize = 256
      source.connect(anal)
      analyser.current = anal

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : ''
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)
      mediaRecorder.current = recorder
      chunks.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data)
      }
      recorder.start(200)

      startedAt.current = Date.now()
      heardSpeech.current = false
      lastSpeechAt.current = null
      setRecording(true)

      const data = new Uint8Array(anal.frequencyBinCount)
      levelTimer.current = setInterval(() => {
        if (!analyser.current) return
        analyser.current.getByteTimeDomainData(data)
        let sum = 0
        for (let i = 0; i < data.length; i++) {
          const v = (data[i]! - 128) / 128
          sum += v * v
        }
        const rms = Math.sqrt(sum / data.length)
        const level = Math.max(0.04, Math.min(1, rms * 4))
        setLevels((prev) => {
          const next = prev.slice(1)
          next.push(level)
          return next
        })

        const now = Date.now()
        const elapsed = now - startedAt.current
        // Rough speech gate on RMS
        if (rms > 0.04) {
          heardSpeech.current = true
          lastSpeechAt.current = now
        } else if (
          heardSpeech.current &&
          lastSpeechAt.current != null &&
          elapsed >= MIN_RECORD_MS &&
          now - lastSpeechAt.current >= SILENCE_MS &&
          !autoSendLock.current
        ) {
          void (async () => {
            const text = await finalizeRecording('send')
            if (text && onAutoSendRef.current) onAutoSendRef.current(text)
          })()
        }
      }, 50)
    } catch (err) {
      clearMeter()
      stopMediaTracks()
      setRecording(false)
      setVoiceError(err instanceof Error ? err.message : 'Could not access microphone')
    }
  }, [clearMeter, finalizeRecording, stopMediaTracks])

  const sendRecording = useCallback(async () => finalizeRecording('send'), [finalizeRecording])
  const cancelRecording = useCallback(async () => {
    await finalizeRecording('cancel')
  }, [finalizeRecording])

  const setOnAutoSend = useCallback((fn: ((text: string) => void) | null) => {
    onAutoSendRef.current = fn
  }, [])

  useEffect(() => {
    return () => {
      void stopSpeaking()
      clearMeter()
      try {
        mediaRecorder.current?.stop()
      } catch {
        // ignore
      }
      stopMediaTracks()
    }
  }, [clearMeter, stopMediaTracks])

  const playbackPending = heldKeys.size > 0 || speaking

  return {
    muted,
    setMuted,
    toggleMute,
    speaking,
    playingKey,
    voiceError,
    heldKeys,
    playbackPending,
    recording,
    voiceBusy,
    levels,
    speakAssistant,
    togglePlayMessage,
    stop,
    startRecording,
    sendRecording,
    cancelRecording,
    setOnAutoSend,
  }
}
